import { useEffect, useCallback } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { platform } from '@/lib/platform';
import { supabase } from '@/integrations/supabase/client';

export function useNativePush() {
  const registerPush = useCallback(async () => {
    if (!platform.isNative) return;

    // Request permission
    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') return;

    // Register with OS
    await PushNotifications.register();
  }, []);

  useEffect(() => {
    if (!platform.isNative) return;

    // Token received - store in Supabase
    const tokenListener = PushNotifications.addListener(
      'registration',
      async (token) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        await supabase.from('push_tokens').upsert({
          user_id: user.id,
          token: token.value,
          platform: platform.isIOS ? 'ios' : 'android',
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,token' });
      }
    );

    // Registration error
    const errorListener = PushNotifications.addListener(
      'registrationError',
      (error) => {
        console.error('Push registration error:', error);
      }
    );

    // Notification received while app is open
    const receivedListener = PushNotifications.addListener(
      'pushNotificationReceived',
      (notification) => {
        // Show in-app notification toast
        console.log('Push received:', notification);
      }
    );

    // Notification tapped
    const actionListener = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action) => {
        // Navigate to the relevant route
        const route = action.notification.data?.route;
        if (route) {
          window.location.href = route;
        }
      }
    );

    registerPush();

    return () => {
      tokenListener.then(l => l.remove());
      errorListener.then(l => l.remove());
      receivedListener.then(l => l.remove());
      actionListener.then(l => l.remove());
    };
  }, [registerPush]);

  return { registerPush };
}
